/**
 * amCharts 5 Exporting plugin — rasterizes the chart root to PNG (`export()`).
 * Plugin: `libs/amcharts5/plugins/exporting.js`.
 */
// @ts-ignore — IIFE registers `window.am5plugins_exporting`
import "../../../libs/amcharts5/plugins/exporting";
import {
  TARS_CHART_PNG_EXPORT_MIN_HEIGHT,
  TARS_CHART_PNG_EXPORT_MIN_WIDTH,
} from "../tars-constants";

/** amCharts 5 `IExportingImageOptions`: lossless-ish PNG + full device pixel canvas. */
const TARS_AM5_PNG_EXPORT_IMAGE_OPTIONS = {
  quality: 1,
  maintainPixelRatio: false,
  minWidth: TARS_CHART_PNG_EXPORT_MIN_WIDTH,
  minHeight: TARS_CHART_PNG_EXPORT_MIN_HEIGHT,
};

export type Am5ChartExportApi = {
  exportPngBlob: () => Promise<Blob>;
  copyToClipboard: () => Promise<void>;
};

/** Attach Exporting to an existing Root; disposing the root disposes Exporting. */
export function attachAm5ExportingToRoot(chartRoot: any): Am5ChartExportApi {
  const ExportingCtor = (window as any).am5plugins_exporting?.Exporting;
  if (typeof ExportingCtor?.new !== "function") {
    console.warn("TARS charts: am5 Exporting plugin not available");
    const unavailable = async () => {
      throw new Error("Chart export is not available.");
    };
    return { exportPngBlob: unavailable, copyToClipboard: unavailable };
  }
  const exporting = ExportingCtor.new(chartRoot, {
    pngOptions: { ...TARS_AM5_PNG_EXPORT_IMAGE_OPTIONS },
  });

  const exportPngBlob = async (): Promise<Blob> => {
    const dataUrl: string = await exporting.export("png", {
      ...TARS_AM5_PNG_EXPORT_IMAGE_OPTIONS,
    });
    const res = await fetch(dataUrl);
    return res.blob();
  };

  const copyToClipboard = async (): Promise<void> => {
    const blob = await exportPngBlob();
    await copyPngBlobToClipboard(blob);
  };

  return { exportPngBlob, copyToClipboard };
}

async function copyPngBlobToClipboard(blob: Blob): Promise<void> {
  if (!navigator.clipboard?.write) {
    throw new Error(
      "Clipboard is not available (use HTTPS or localhost, or grant permission)."
    );
  }
  await navigator.clipboard.write([new ClipboardItem({ "image/png": blob })]);
}
